import type { NextPage, GetStaticPropsContext } from 'next'
import { useMemo } from 'react'
import { orderBy, uniq } from 'lodash'
import { useTranslations } from 'next-intl'

import { useQuery } from '@apollo/client'

import { withAuthProtection } from '@/features/auth/withAuthProtection'
import AddPoolAccountModal from '@/features/mining-pool/AddPoolAccountModal'
import MiningPoolsTable from '@/features/mining-pool/MiningPoolsTable'

import { getUserMiningPools } from '@/api/miningPool'

import { Loading } from '@/components/Loading'

import {
  Text,
  Center,
  Box,
  Stack,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react'

import SetPoolAccount from '@/features/mining-pool/SetDefaultPool'
import { EmptyAlgorithm } from '@/features/mining-pool/EmptyAlgorithm'
import { IMiningPoolNode } from '@/types/miningPool.interface'

const ALGORITHM_MAP: Record<string, string> = {
  SHA256: 'dashboard.mining-pool.algorithm-sha256',
  Equihash: 'dashboard.mining-pool.algorithm-equihash',
  Scrypt: 'dashboard.mining-pool.algorithm-scrypt',
  Blake2_S: 'dashboard.mining-pool.algorithm-blake2s',
  Handshake: 'dashboard.mining-pool.algorithm-handshake',
  Ethash: 'dashboard.mining-pool.algorithm-ethash',
}

const MiningPoolSettingsPage: NextPage = () => {
  const t = useTranslations()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const gray900 = useColorModeValue('gray.900', 'gray.100')

  const { data: userMiningPoolsData, loading: userMiningPoolsLoading } =
    useQuery(getUserMiningPools)

  const miningPools: IMiningPoolNode[] =
    userMiningPoolsData?.userMiningPools?.nodes ?? []

  const orderedAlgorithms = useMemo<string[]>(
    () =>
      orderBy(
        // Unique algorithms present in the user's pools
        uniq(miningPools.map((pool) => pool.miningPoolAlgorithm)),
        // SHA256 pinned first
        (algorithm) => (algorithm === 'SHA256' ? 0 : 1),
        // and the rest sorted alphabetically
        'asc'
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userMiningPoolsData]
  )

  const renderAlgorithmSection = (algorithm: string, index: number) => {
    const poolsForAlgorithm = miningPools.filter(
      (pool) => pool.miningPoolAlgorithm === algorithm
    )
    const algorithmTitle =
      ALGORITHM_MAP[algorithm] ??
      `dashboard.mining-pool.algorithm-${algorithm.toLowerCase()}`

    return (
      <Box key={algorithm}>
        <Text
          mt={index === 0 ? 0 : 4}
          color={gray900}
          fontWeight={600}
          borderBottom={'1px solid gray.220'}
          padding={'16px 0px'}
          display="flex"
          alignItems="flex-start"
          gap="10px"
          alignSelf={'stretch'}
        >
          {t(algorithmTitle)}
        </Text>
        <Stack align="stretch" mb="8">
          <MiningPoolsTable
            userMiningPools={{ userMiningPools: { nodes: poolsForAlgorithm } }}
          />
        </Stack>
      </Box>
    )
  }

  if (userMiningPoolsLoading) return <Loading />

  return (
    <Center
      w="full"
      p={{ base: 0, md: 5 }}
      flexDirection="column"
      py={5}
      overflowX="hidden"
    >
      <Box
        width="full"
        p={{ base: '3', md: '5' }}
        rounded="lg"
        mb={8}
        pt={5}
        overflowX="hidden"
      >
        <SetPoolAccount onOpen={onOpen} />

        {orderedAlgorithms.length === 0 ? (
          <EmptyAlgorithm
            title={t('dashboard.mining-pool.set-default-pool')}
            description={t('dashboard.mining-pool.empty-pool-description')}
            buttonLabel={t('dashboard.mining-pool.add-pool-account')}
            onClick={onOpen}
          />
        ) : (
          orderedAlgorithms.map((algorithm, index) =>
            renderAlgorithmSection(algorithm, index)
          )
        )}

        <AddPoolAccountModal
          isOpen={isOpen}
          onClose={onClose}
          userMiningPools={userMiningPoolsData}
        />
      </Box>
    </Center>
  )
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`../../../../locales/${locale}.json`)).default,
    },
  }
}

export default withAuthProtection(MiningPoolSettingsPage)
